/**
 * Fastlane Club Co. — sticky mobile add-to-cart bar.
 *
 * Shows a fixed bottom bar whenever the real buy area (.buy-buttons-block)
 * is out of the viewport, mobile only (the CSS gates it under 750px; desktop
 * already has a sticky details column).
 *
 * The bar's button never proxies clicks: it is form-associated with the real
 * product form (form="BuyButtons-ProductForm-<sectionId>", type=submit,
 * name=add) — the same native pattern Horizon's cart checkout button uses —
 * so ProductFormComponent's submit handler, the cart drawer auto-open, and
 * browser validation all behave exactly as if the in-page button was pressed.
 *
 * The disabled state of the real button (sold out / unavailable combination)
 * is mirrored with a MutationObserver rather than by reimplementing variant
 * logic.
 */
class FccStickyAtc extends HTMLElement {
  #io = null;
  #mo = null;

  connectedCallback() {
    const section = this.closest('.shopify-section') || document;
    this.buyBlock = section.querySelector('.buy-buttons-block');
    this.realButton = section.querySelector('add-to-cart-component button[ref="addToCartButton"]');
    this.barButton = this.querySelector('button');

    if (!this.buyBlock || !this.barButton) return;

    this.#io = new IntersectionObserver(
      ([entry]) => {
        this.toggleAttribute('visible', !entry.isIntersecting);
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    this.#io.observe(this.buyBlock);

    if (this.realButton) {
      this.#mirrorDisabled();
      this.#mo = new MutationObserver(() => this.#mirrorDisabled());
      this.#mo.observe(this.realButton, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#mo?.disconnect();
  }

  #mirrorDisabled() {
    const off = this.realButton.hasAttribute('disabled');
    this.barButton.toggleAttribute('disabled', off);
    this.barButton.textContent = off
      ? this.barButton.dataset.soldOutLabel
      : this.barButton.dataset.addLabel;
  }
}

if (!customElements.get('fcc-sticky-atc')) {
  customElements.define('fcc-sticky-atc', FccStickyAtc);
}
